import segment from '@analytics/segment'
import { proxyServiceURLOrigin } from '@/services/Partytown'
import { ECOMMERCE_EVENTS, EcommPayload } from '@/utils/analytics/ecommerce-events'
import { namedLogger } from '../../utils'
import { CustomFacebookPixels } from './custom-facebook-pixels'
import { generateCustomFacebookPixel } from './plugins/custom-facebook-pixel-plugin'
import { KochavaPlugin } from './plugins/kochava-plugin'

const logger = namedLogger('segment Analytics Plugin', {
  debug: false,
})

// Custom batching for high-volume events like content_interaction (impressions)
// This reduces API calls by batching events before sending to Segment
const BATCH_CONFIG = {
  maxSize: parseInt(process.env.NEXT_PUBLIC_SEGMENT_BATCH_MAX_SIZE || '20', 10),
  flushIntervalMs: parseInt(process.env.NEXT_PUBLIC_SEGMENT_BATCH_FLUSH_INTERVAL_MS || '10000', 10),
  eventsToBatch: ['content_interaction'] as const, // Events to batch
}

type BatchedEvent = {
  event: string
  properties: Record<string, unknown>
  userId?: string
  anonymousId?: string
  integrations?: Record<string, unknown>
}

// Batch state - module-level to persist across track calls
let eventBatch: BatchedEvent[] = []
let flushTimeout: ReturnType<typeof setTimeout> | null = null
let isInitialized = false

// Segment batch API endpoint - can be overridden via env var for proxy support
const SEGMENT_BATCH_API_URL = process.env.NEXT_PUBLIC_SEGMENT_BATCH_API_URL || `${proxyServiceURLOrigin}/seg/v1/batch`

/**
 * Gets the Segment analytics client from the window object
 */
function getSegmentClient(): SegmentAnalytics | null {
  return typeof window !== 'undefined' ? (window as WindowWithAnalytics).analytics ?? null : null
}

/**
 * Sends events via the Segment client (client.track).
 * Used for normal flush operations when the page is not unloading.
 */
function sendEventsViaClient(events: BatchedEvent[]): void {
  const client = getSegmentClient()
  if (!client) {
    logger.warn('Cannot flush batch: Segment client not available')
    return
  }

  events.forEach((event) => {
    try {
      client.track(
        {
          event: event.event,
          properties: event.properties,
          ...(event.userId ? { userId: event.userId } : { anonymousId: event.anonymousId }),
        },
        { integrations: event.integrations },
      )
    } catch (error) {
      logger.error('Failed to track batched event', { event: event.event }, error)
    }
  })
}

/**
 * Flushes batched events using sendBeacon for reliable delivery during page unload.
 * sendBeacon is purpose-built for this use case and is more reliable than fetch during teardown.
 * Falls back to fetch with keepalive if sendBeacon is unavailable or fails,
 * then to regular client.track as a last resort.
 */
function flushBatchWithBeacon(events: BatchedEvent[]): void {
  const writeKey = process.env.NEXT_PUBLIC_SEGMENT_KEY
  if (!writeKey) {
    logger.warn('Cannot use beacon flush: Segment write key not available')
    sendEventsViaClient(events)
    return
  }

  try {
    const batch = events.map((event) => ({
      type: 'track' as const,
      event: event.event,
      properties: event.properties,
      userId: event.userId,
      anonymousId: event.anonymousId,
      integrations: event.integrations,
      timestamp: new Date().toISOString(),
    }))

    const payload = JSON.stringify({ batch })

    // Try sendBeacon first — it was purpose-built for page unload delivery
    // and is far more reliable than fetch with keepalive during teardown
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' })
      const beaconUrl = `${SEGMENT_BATCH_API_URL}?writeKey=${encodeURIComponent(writeKey)}`
      const beaconSuccess = navigator.sendBeacon(beaconUrl, blob)

      if (beaconSuccess) {
        logger.debug('Flushed batch with sendBeacon', { count: events.length })
        return
      }

      // sendBeacon can fail if the payload exceeds the browser's limit (~64KB)
      logger.debug('sendBeacon failed, falling back to fetch with keepalive', { count: events.length })
    }

    // Fallback to fetch with keepalive
    fetch(SEGMENT_BATCH_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Basic ${btoa(writeKey + ':')}`,
      },
      body: payload,
      keepalive: true,
    }).catch((fetchError) => {
      logger.debug('Fetch with keepalive also failed, falling back to client.track', fetchError)
      sendEventsViaClient(events)
    })

    logger.debug('Flushed batch with keepalive fetch', { count: events.length })
  } catch (error) {
    logger.warn('Failed to flush with beacon, falling back to client.track', error)
    sendEventsViaClient(events)
  }
}

/**
 * Flushes all batched events to Segment immediately.
 * Called when batch is full, timeout expires, or page lifecycle events occur.
 *
 * @param useBeacon - If true, uses sendBeacon (with fetch+keepalive and client.track fallbacks) for reliable unload delivery.
 *                    Should be true for page lifecycle events (beforeunload, pagehide, visibilitychange).
 */
function flushBatch(useBeacon = false): void {
  if (eventBatch.length === 0) return

  const eventsToSend = [...eventBatch]
  eventBatch = []

  if (flushTimeout) {
    clearTimeout(flushTimeout)
    flushTimeout = null
  }

  logger.debug('Flushing batch', { count: eventsToSend.length, useBeacon })

  if (useBeacon && typeof window !== 'undefined') {
    flushBatchWithBeacon(eventsToSend)
  } else {
    sendEventsViaClient(eventsToSend)
  }
}

/**
 * Schedules a flush to occur after the configured interval.
 * Only schedules if no flush is already pending.
 */
function scheduleFlush(): void {
  if (flushTimeout) return

  flushTimeout = setTimeout(() => {
    flushTimeout = null
    flushBatch()
  }, BATCH_CONFIG.flushIntervalMs)
}

/**
 * Adds an event to the batch queue.
 * Triggers immediate flush if batch size limit is reached, otherwise schedules a delayed flush.
 * Validates that the event has required fields before adding.
 */
function addToBatch(event: BatchedEvent): void {
  // Validate required fields
  if (!event.event) {
    logger.warn('Cannot batch event: missing event name')
    return
  }
  if (!event.userId && !event.anonymousId) {
    logger.warn('Cannot batch event: missing userId and anonymousId', { event: event.event })
    return
  }

  eventBatch.push(event)
  logger.debug('Added to batch', { batchSize: eventBatch.length, event: event.event })

  if (eventBatch.length >= BATCH_CONFIG.maxSize) {
    flushBatch()
  } else {
    scheduleFlush()
  }
}

/**
 * Initializes event listeners that flush the batch on page lifecycle events.
 * Ensures batched events are sent before the user leaves the page.
 * Uses fetch with keepalive for reliable delivery during unload.
 */
function initBatchFlushListeners(): void {
  if (isInitialized || typeof window === 'undefined') return
  isInitialized = true

  // Flush on page unload - use beacon for reliable delivery
  window.addEventListener('beforeunload', () => {
    flushBatch(true)
  })

  // Flush when page becomes hidden (mobile tab switching, etc.) - use beacon
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushBatch(true)
    }
  })

  // Flush on pagehide (more reliable on mobile) - use beacon
  window.addEventListener('pagehide', () => {
    flushBatch(true)
  })
}

type SegmentAnalytics = {
  track: (
    data: { event: string; properties: Record<string, unknown>; userId?: string; anonymousId?: string },
    options?: { integrations?: Record<string, unknown> },
  ) => void
  ready: (callback: () => void) => void
  register: (plugin: unknown) => void
}

type WindowWithAnalytics = Window & { analytics?: SegmentAnalytics }

export function segmentPlugin() {
  const _segmentPlugin = segment({
    syncAnonymousId: true,
    writeKey: process.env.NEXT_PUBLIC_SEGMENT_KEY,

    customScriptSrc: `${proxyServiceURLOrigin}/segment/analytics.js/v1/${process.env.NEXT_PUBLIC_SEGMENT_KEY}/analytics.min.js`,
  })

  logger.debug('_segmentPlugin', { _segmentPlugin })

  const plugins = [
    ...Object.entries(CustomFacebookPixels).map(([pixelName, pixelSettings]) => {
      logger.debug('registering pixel', { pixelName })
      return generateCustomFacebookPixel(pixelName, pixelSettings)
    }),
    KochavaPlugin({ appGuid: process.env.NEXT_PUBLIC_KOCHAVA_APP_GUID }),
  ]

  return {
    ..._segmentPlugin,
    loaded: (...args: unknown[]) => {
      try {
        const results = [
          _segmentPlugin?.loaded?.(...args),
          ...plugins.map((plugin) => {
            const loaded = plugin.isLoaded()
            logger.debug('plugin loaded', { loaded })
            return loaded
          }),
        ]
        logger.debug('results', { results })
        return results.every((result) => result)
      } catch (error) {
        logger.error('Error in loaded', undefined, error)
        return false
      }
    },
    ready: (...args: unknown[]) => {
      try {
        _segmentPlugin?.ready?.(...args)

        const results = [...plugins.map((plugin) => plugin.isLoaded())]
        logger.debug('results', { results })
        return results.every((result) => result)
      } catch (error) {
        logger.error('Error in ready', undefined, error)
        return false
      }
    },
    initialize: (...args: unknown[]) => {
      _segmentPlugin.initialize(...args)
      // Initialize batch flush listeners for content_interaction events
      initBatchFlushListeners()
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      window.analytics?.ready?.(() => {
        plugins.forEach((plugin) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          window.analytics?.register?.(plugin)
        })
      })
    },
    track: (params: {
      payload: {
        event: string
        userId?: string
        anonymousId?: string
        properties: {
          experiments?: unknown
          funnel?: EcommPayload['funnel']
          projectSlug?: string
        }
      }
      config?: {
        disableAnonymousTraffic?: boolean
      }
    }) => {
      // node_modules/@analytics/segment/lib/analytics-plugin-segment.es.js:127 track()
      const client = getSegmentClient()
      if (!client) {
        return _segmentPlugin.track(params)
      }
      const payload = params.payload,
        config = params.config
      const userId = payload.userId,
        anonymousId = payload.anonymousId

      if (!userId && !anonymousId) {
        const error = new Error('Missing userId and anonymousId. You must include one to make segment call')
        logger.error(error.message, undefined, error)
        throw error
      }

      if (config?.disableAnonymousTraffic && !userId) {
        return false
      }

      // Build integrations options for this event
      const integrations = {
        // Custom Facebook Pixels block ecommerce spec events if enabled on plugin
        ...(ECOMMERCE_EVENTS.includes(params?.payload?.event as (typeof ECOMMERCE_EVENTS)[number])
          ? (Object.fromEntries(
              Object.entries(CustomFacebookPixels)
                .filter(([, pixelSettings]) => pixelSettings.allowEcommerceSpecEvents !== false)
                .map(([pixelName]) => [pixelName, false]),
            ) as object)
          : {}),
        // Facebook Pixel block ecommerce spec events if enabled
        // "Facebook Pixel" => Streaming Facebook Pixel configured in segment
        ...(ECOMMERCE_EVENTS.includes(params?.payload?.event as (typeof ECOMMERCE_EVENTS)[number]) &&
        process.env.NEXT_PUBLIC_STREAMING_PIXEL_CUSTOM_FACEBOOK_PIXEL_BLOCK_ECOMMERCE_SPEC_EVENTS_ENABLED === 'true'
          ? {
              'Facebook Pixel': false,
            }
          : {}),
      }

      // Check if this event should be batched
      const shouldBatch = BATCH_CONFIG.eventsToBatch.includes(
        payload.event as (typeof BATCH_CONFIG.eventsToBatch)[number],
      )

      if (shouldBatch) {
        // Add to batch for deferred sending
        addToBatch({
          event: payload.event,
          properties: payload.properties as Record<string, unknown>,
          userId,
          anonymousId,
          integrations,
        })
      } else {
        // Send immediately for non-batched events
        const data = {
          event: payload.event,
          properties: payload.properties,
          ...(userId ? { userId } : { anonymousId }),
        }

        client.track(data, { integrations })

        if (
          !!params.payload.event &&
          !!params?.payload?.properties?.funnel &&
          ECOMMERCE_EVENTS.includes(params?.payload?.event as (typeof ECOMMERCE_EVENTS)[number])
        ) {
          const eventName = `${params.payload.event} - ${params.payload.properties.funnel}`
          client.track({ ...data, event: eventName })
        }
      }
    },
    reset: () => {
      // Flush any pending batched events before reset
      flushBatch()
      _segmentPlugin.reset()
      plugins.forEach((plugin) => {
        if ('reset' in plugin && typeof plugin.reset === 'function') {
          plugin.reset()
        }
      })
    },
  }
}
